export type RapidEventType =
  | "seizure_onset"
  | "seizure_offset"
  | "medication_administered"
  | "procedure_marker"
  | "clinical_note";

export type RapidEventDraftInput = {
  eventType: RapidEventType;
  medicationName?: string;
  medicationDose?: string;
  medicationRoute?: string;
  procedureName?: string;
  reason?: string;
  expectedEffect?: string;
  followUpWindowSeconds?: string | number;
};

export type RapidEventValidationResult = {
  isValid: boolean;
  error: string | null;
  normalized: {
    eventType: RapidEventType;
    medicationName?: string;
    medicationDose?: string;
    medicationRoute?: string;
    procedureName?: string;
    reason?: string;
    expectedEffect?: string;
    followUpWindowSeconds?: number;
  };
};

const MAX_TEXT_LENGTH = 512;
const MAX_FOLLOWUP_WINDOW_SECONDS = 24 * 60 * 60;

function cleanText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export function validateRapidEventDraft(
  draft: RapidEventDraftInput,
): RapidEventValidationResult {
  const eventType = draft.eventType;
  const medicationName = cleanText(draft.medicationName);
  const medicationDose = cleanText(draft.medicationDose);
  const medicationRoute = cleanText(draft.medicationRoute);
  const procedureName = cleanText(draft.procedureName);
  const reason = cleanText(draft.reason);
  const expectedEffect = cleanText(draft.expectedEffect);

  const followUpRaw =
    typeof draft.followUpWindowSeconds === "number"
      ? draft.followUpWindowSeconds
      : Number(String(draft.followUpWindowSeconds ?? "").trim());

  const followUpWindowSeconds = Number.isFinite(followUpRaw)
    ? Math.max(0, Math.floor(followUpRaw))
    : undefined;

  if (
    followUpWindowSeconds !== undefined &&
    followUpWindowSeconds > MAX_FOLLOWUP_WINDOW_SECONDS
  ) {
    return {
      isValid: false,
      error: `Follow-up window must be <= ${MAX_FOLLOWUP_WINDOW_SECONDS} seconds.`,
      normalized: { eventType },
    };
  }

  if (
    (reason && reason.length > MAX_TEXT_LENGTH) ||
    (expectedEffect && expectedEffect.length > MAX_TEXT_LENGTH)
  ) {
    return {
      isValid: false,
      error: `Reason/Expected effect must be <= ${MAX_TEXT_LENGTH} characters.`,
      normalized: { eventType },
    };
  }

  if (eventType === "medication_administered" && !medicationName) {
    return {
      isValid: false,
      error: "Medication name is required for medication events.",
      normalized: { eventType },
    };
  }

  if (eventType === "procedure_marker" && !procedureName) {
    return {
      isValid: false,
      error: "Procedure name is required for procedure markers.",
      normalized: { eventType },
    };
  }

  if (eventType === "clinical_note" && !reason) {
    return {
      isValid: false,
      error: "Clinical note text is required.",
      normalized: { eventType },
    };
  }

  return {
    isValid: true,
    error: null,
    normalized: {
      eventType,
      medicationName,
      medicationDose,
      medicationRoute,
      procedureName,
      reason,
      expectedEffect,
      ...(followUpWindowSeconds !== undefined
        ? { followUpWindowSeconds }
        : {}),
    },
  };
}
